import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Loader2, Sparkles, Type } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../config';
import GeneratedImagesInline from '../components/GeneratedImagesInline';
import LoadingAnimation from '../components/LoadingAnimation';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import { loadLastCrearAnuncioState, mergeLastCrearAnuncioState, type CrearAnuncioObjetivo } from '../utils/crearAnuncioStorage';
import { trackInteraction } from '../utils/trackInteraction';

interface Composicion {
  id: number;
  link_img: string;
  categoria: string;
  nombre?: string;
}

const LAST_COMPOSITION_STORAGE_KEY = 'atlas:last-personalized-composition';
const LAST_COMPOSITION_TTL_MS = 1000 * 60 * 60 * 6; // 6 hours

interface LastCompositionPersistedState {
  textEnabled: boolean;
  selectedCompId: number | null;
  selectedCompUrl?: string;
  compositionText: string;
  idsKey: string;
  modo: 'rapido' | 'personalizado';
  composicion: 'modelo' | 'producto';
  tipo: 'imagen' | 'video';
  timestamp: number;
}

export const SeleccionarComposicionAnuncio: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(() => idsParam.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => s !== '0'), [idsParam]);
  const modo = (params.get('modo') as 'rapido' | 'personalizado') || 'personalizado';
  const composicion = (params.get('composicion') as 'modelo' | 'producto') || 'modelo';
  const tipo = (params.get('tipo') as 'imagen' | 'video') || 'imagen';
  const formato = (params.get('formato') as 'post' | 'story' | 'square') || 'post';
  const cantidadImagenesRaw = params.get('cantidad_imagenes');
  const cantidadImagenes = useMemo(() => {
    const n = Number(cantidadImagenesRaw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [cantidadImagenesRaw]);
  const modoPro = params.get('modo_pro') === '1';

  // Modelo params forwarded
  const modeloMode = (params.get('modelo_mode') as 'ia' | 'guardados' | 'personalizado') || 'ia';
  const modeloGuardadoId = params.get('modelo_guardado_id') || '';
  const modeloGuardadoNombre = params.get('modelo_guardado_nombre') || '';
  const pose = params.get('pose') || '';
  const genero = params.get('genero') || '';
  const edad = params.get('edad') || '';
  const tipoCuerpo = params.get('tipo_cuerpo') || '';
  const tonoPiel = params.get('tono_piel') || '';
  const colorPelo = params.get('color_pelo') || '';
  const largoPelo = params.get('largo_pelo') || '';
  const descModelo = params.get('desc_modelo') || '';

  // Background params forwarded
  const backgroundMode = (params.get('background_mode') as 'ia' | 'blanco' | 'elegido' | 'descripcion') || 'blanco';
  const backgroundUrl = params.get('background_url') || '';
  const backgroundIdParam = params.get('background_id');
  const backgroundId = backgroundIdParam ? Number(backgroundIdParam) : undefined;
  const backgroundTextParam = params.get('background_text') || '';
  const objetivo = params.get('objetivo') || '';
  const detallesOferta = params.get('detalles_oferta') || '';

  // Local state for composiciones
  const [composiciones, setComposiciones] = useState<Composicion[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [textEnabled, setTextEnabled] = useState<boolean>(false);
  const [selectedCompUrl, setSelectedCompUrl] = useState<string>('');
  const [selectedCompId, setSelectedCompId] = useState<number | null>(null);
  const [compositionText, setCompositionText] = useState<string>('');
  const [generating, setGenerating] = useState<boolean>(false);
  const [completeProgressNow, setCompleteProgressNow] = useState<boolean>(false);
  const [genError, setGenError] = useState<React.ReactNode | null>(null);
  const consecutiveGenErrorsRef = useRef<number>(0);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [generatedImageIds, setGeneratedImageIds] = useState<number[]>([]);
  const [companyIdForImages, setCompanyIdForImages] = useState<string>('');
  const willShowResultsRef = useRef<boolean>(false);
  // Prevent click-through when closing results
  const [clickShield, setClickShield] = useState<boolean>(false);
  const savedState = useMemo(() => loadLastCrearAnuncioState(), []);
  const FASHION_LABEL = 'Moda e Indumentaria';
  const isFashion = ((savedState?.rubro as string | undefined) ?? FASHION_LABEL) === FASHION_LABEL;
  const idsKey = useMemo(() => {
    if (ids.length === 0) return '';
    return [...ids].sort().join('-');
  }, [ids]);

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    const allowed: CrearAnuncioObjetivo[] = ['mostrar', 'oferta', 'lanzamiento', 'evento'];
    const val = objetivo as CrearAnuncioObjetivo;
    const objetivoValidated = allowed.includes(val) ? val : undefined;

    mergeLastCrearAnuncioState({
      modo,
      selectedVariantIds: ids,
      ...(objetivoValidated !== undefined ? { objetivo: objetivoValidated } : {}),
      detallesOferta: detallesOferta.trim() ? detallesOferta.trim() : undefined,
      composicion,
      tipo,
      formato,
      cantidadImagenes: cantidadImagenes ?? undefined,
    });
  }, [ids, modo, objetivo, detallesOferta, composicion, tipo, formato, cantidadImagenes]);
  // Progress duration differs by mode: 70s for quick, 35s for personalized
  const progressDurationMs = useMemo(() => (modo === 'rapido' ? 80000 : 35000), [modo]);
  // Generate enabled when toggle is off, or when on and a template is selected
  const canGenerateNow = useMemo(() => {
    if (!textEnabled) return true;
    return Boolean(selectedCompId);
  }, [textEnabled, selectedCompId]);

  useEffect(() => {
    if (modo !== 'personalizado') return;
    if (!idsKey) return;
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(LAST_COMPOSITION_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as LastCompositionPersistedState | null;
      if (!parsed) return;
      if (!parsed.timestamp || Date.now() - parsed.timestamp > LAST_COMPOSITION_TTL_MS) {
        window.localStorage.removeItem(LAST_COMPOSITION_STORAGE_KEY);
        return;
      }
      if (parsed.idsKey !== idsKey || parsed.modo !== modo || parsed.composicion !== composicion || parsed.tipo !== tipo) {
        return;
      }
      setTextEnabled(Boolean(parsed.textEnabled));
      setSelectedCompId(typeof parsed.selectedCompId === 'number' ? parsed.selectedCompId : null);
      setSelectedCompUrl(parsed.selectedCompUrl || '');
      setCompositionText(parsed.compositionText || '');
    } catch (error) {
      console.error('Error restoring last composition state:', error);
    }
  }, [modo, idsKey, composicion, tipo]);

  const saveLastCompositionState = useCallback(() => {
    if (modo !== 'personalizado') return;
    if (!idsKey) return;
    if (typeof window === 'undefined') return;
    const payload: LastCompositionPersistedState = {
      textEnabled,
      selectedCompId,
      selectedCompUrl,
      compositionText,
      idsKey,
      modo,
      composicion,
      tipo,
      timestamp: Date.now(),
    };

    try {
      window.localStorage.setItem(LAST_COMPOSITION_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error('Error saving last composition state:', error);
    }

    mergeLastCrearAnuncioState({
      composition: {
        enabled: textEnabled,
        selectedCompId,
        selectedCompUrl,
        compositionText,
      },
    });
  }, [modo, idsKey, composicion, tipo, textEnabled, selectedCompId, selectedCompUrl, compositionText]);

  const handleReturnToConfig = useCallback(() => {
    try { console.log('[SeleccionarComposicionAnuncio] handleReturnToConfig called', { modo, pathname: window.location.pathname, search: window.location.search }); } catch {}
    // Enable click shield and delay hiding results to avoid click-through on underlying buttons
    setClickShield(true);
    setTimeout(() => setShowResults(false), 50);
    setTimeout(() => setClickShield(false), 300);
    try { console.log('[SeleccionarComposicionAnuncio] navigating to seleccionar-productos after closing results'); } catch {}
    const persisted = loadLastCrearAnuncioState();
    const variantIds = persisted?.selectedVariantIds && persisted.selectedVariantIds.length > 0 ? persisted.selectedVariantIds : ids;
    const nextModo = persisted?.modo || modo;
    const qp = new URLSearchParams();
    if (variantIds.length > 0) qp.set('ids', variantIds.join(','));
    if (nextModo) qp.set('modo', nextModo);
    const query = qp.toString();
    navigate(query ? `/redes-sociales/crear-anuncio/seleccionar-productos?${query}` : '/redes-sociales/crear-anuncio/seleccionar-productos');
  }, [ids, modo, navigate]);

  // Subscription limit modal state (same UX/messages as Estudio Virtual)
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');

  // Helpers copied from ImageConfiguration for billing cycle anchored to paid_plan_start_date
  const getBillingCycleRange = (paidPlanStartDate: string) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const anchorDay = subscriptionDate.getDate();

    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };

    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }

    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(
        subscriptionDate.getFullYear(),
        subscriptionDate.getMonth(),
        subscriptionDate.getDate(),
        0, 0, 0, 0
      );
    }

    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const checkSubscriptionLimits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, paid_plan_start_date')
        .eq('id_company', profile.id_company)
        .single();
      if (!companyProfile) throw new Error('No company profile found');

      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error checking subscription limits:', err);
      return true; // Do not block on error
    }
  };

  useEffect(() => {
    const fetchComposiciones = async () => {
      try {
        setLoading(true);
        setFetchError(null);
        const { data, error } = await supabase
          .schema('public')
          .from('composiciones')
          .select('id, link_img, categoria, nombre');
        if (error) throw error;
        setComposiciones((data as any) || []);
      } catch (e) {
        console.error('Error cargando composiciones', e);
        setFetchError(e instanceof Error ? e.message : 'Error al cargar composiciones');
      } finally {
        setLoading(false);
      }
    };
    fetchComposiciones();
  }, []);

  const compsPorCategoria = useMemo(() => {
    const map = new Map<string, Composicion[]>();
    for (const c of composiciones) {
      const key = c.categoria || 'Otros';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [composiciones]);

  const [activeCategoria, setActiveCategoria] = useState<string>('');
  const categorias = useMemo(() => compsPorCategoria.map(([c]) => c), [compsPorCategoria]);
  useEffect(() => {
    if (!activeCategoria && categorias.length > 0) {
      setActiveCategoria(categorias[0]);
    }
  }, [categorias, activeCategoria]);
  const itemsCategoriaActual = useMemo(() => {
    const entry = compsPorCategoria.find(([c]) => c === activeCategoria);
    return entry ? entry[1] : [];
  }, [compsPorCategoria, activeCategoria]);

  const pickComposicion = (url: string, id?: number) => {
    setSelectedCompUrl(url);
    if (typeof id === 'number') setSelectedCompId(id); else setSelectedCompId(null);
  };

  // Preload images by fetching signed URLs and loading them into browser cache
  type PreloadedImage = { id: number; fileName: string; signedUrl: string; favorito: boolean };
  const preloadGeneratedImages = async (imageIds: number[], companyId: string): Promise<PreloadedImage[]> => {
    const maxAttempts = 8;
    const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
    const pending = new Set<number>(imageIds);
    const preloadedImages: PreloadedImage[] = [];

    for (let attempt = 1; attempt <= maxAttempts && pending.size > 0; attempt++) {
      for (const imageId of Array.from(pending)) {
        try {
          const { data: imageData, error: imageError } = await supabase
            .from('imagenes_generadas')
            .select('file_name, favorito')
            .eq('id', imageId)
            .eq('id_company', companyId)
            .single();

          if (imageError || !imageData?.file_name) continue;

          const filePath = `${companyId}/full/${imageData.file_name}`;
          const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
          
          if (signedUrlData?.signedUrl) {
            preloadedImages.push({
              id: imageId,
              fileName: imageData.file_name,
              signedUrl: signedUrlData.signedUrl,
              favorito: imageData.favorito === true,
            });
            pending.delete(imageId);
          }
        } catch (err) {
          console.error(`[preloadGeneratedImages] Error for image ${imageId}:`, err);
        }
      }
      if (pending.size > 0 && attempt < maxAttempts) await delay(1000);
    }

    await Promise.all(
      preloadedImages.map(
        (img) =>
          new Promise<void>((resolve) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => resolve();
            image.src = img.signedUrl;
          })
      )
    );

    return preloadedImages;
  };

  const handleGenerate = async () => {
    if (ids.length === 0 || !canGenerateNow) return;
    try {
      mergeLastCrearAnuncioState({
        selectedVariantIds: ids,
      });
      setGenerating(true);
      setCompleteProgressNow(false);
      setGenError(null);
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) { setGenerating(false); return; }
      try { if (textEnabled && compositionText.trim()) trackInteraction('texto_personalizado'); } catch {}

      if (modo === 'personalizado') {
        saveLastCompositionState();
      }

      // 1) Get user and company_id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');

      // 2) Build format description
      let formatName = '';
      switch (formato) {
        case 'story':
          formatName = '9:16';
          break;
        case 'square':
          formatName = '1:1';
          break;
        case 'post':
        default:
          formatName = '4:5';
      }

      // 3) Determine ids to use
      const variantIds = ids.map((s) => String(s)).map((s) => s.trim()).filter((s) => !!s && s !== '0');
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      // 4) Build request payload (include all selected data)
      const composicionForApi: 'modelo' | 'producto' = (!isFashion && modo === 'personalizado') ? 'producto' : composicion;
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo,
        estilo: 'automatico',
        tipo,
        cantidad_imagenes: tipo === 'imagen' ? (cantidadImagenes ?? undefined) : undefined,
        Format: formatName,
        objetivo: objetivo || undefined,
        detalles_oferta: detallesOferta.trim() ? detallesOferta.trim() : undefined,
        // High-level composition choice (override to 'producto' for non-fashion personalized)
        composicion: composicionForApi,
        // Background selection
        background_mode: backgroundMode,
        background_id:
          (backgroundMode === 'elegido' && typeof backgroundId === 'number' && Number.isFinite(backgroundId))
            ? backgroundId
            : (backgroundMode === 'blanco' && modo === 'personalizado'
                ? 5
                : undefined),
        background_url: backgroundMode === 'elegido' && !(typeof backgroundId === 'number' && Number.isFinite(backgroundId)) ? (backgroundUrl || undefined) : undefined,
        background_text: backgroundMode === 'descripcion' && backgroundTextParam.trim() ? backgroundTextParam.trim() : undefined,
        // Composition selection (only when toggle is enabled)
        composition_mode: textEnabled ? true : false,
        composition_id: textEnabled && typeof selectedCompId === 'number' && Number.isFinite(selectedCompId) ? selectedCompId : undefined,
        composition_url: textEnabled && !(typeof selectedCompId === 'number' && Number.isFinite(selectedCompId)) ? (selectedCompUrl || undefined) : undefined,
        composition_text: textEnabled && compositionText.trim() ? compositionText.trim() : undefined,
        modo_pro: modoPro,
      };

      // Model details (if provided in params)
      if (pose) requestData.pose = pose;
      requestData.modelo_mode = modeloMode;
      if (modeloMode === 'guardados' && modeloGuardadoId) {
        requestData.modelo_guardado_id = modeloGuardadoId;
        if (modeloGuardadoNombre) requestData.modelo_guardado_nombre = modeloGuardadoNombre;
      } else if (modeloMode === 'personalizado') {
        if (genero) requestData.genero = genero;
        if (edad) requestData.edad = edad;
        if (tipoCuerpo) requestData.tipo_cuerpo = tipoCuerpo;
        if (tonoPiel) requestData.tono_piel = tonoPiel;
        if (colorPelo) requestData.color_pelo = colorPelo;
        if (largoPelo) requestData.largo_pelo = largoPelo;
        if (descModelo.trim()) requestData.desc_modelo = descModelo.trim();
      }

      // 5) Call webhook
      try { console.log('[SeleccionarComposicionAnuncio] handleGenerate payload', requestData); } catch {}
      const response = await fetch(GENERATE_AD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData)
      });
      let __respTextCG: string | null = null;
      try { __respTextCG = await response.clone().text(); } catch {}
      try { console.log('[SeleccionarComposicionAnuncio] handleGenerate response', { status: response.status, ok: response.ok, text: __respTextCG }); } catch {}
      if (!response.ok) {
        console.error('[SeleccionarComposicionAnuncio] handleGenerate not ok');
        throw new Error(__respTextCG || `Error ${response.status}`);
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[SeleccionarComposicionAnuncio] handleGenerate json parse error', e); }
      try { console.log('[SeleccionarComposicionAnuncio] handleGenerate json', data); } catch {}
      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        willShowResultsRef.current = true;
        const imageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult = (data.id_company as string) || profile.id_company;
        setGeneratedImageIds(imageIds);
        setCompanyIdForImages(companyIdResult);
        consecutiveGenErrorsRef.current = 0;
        setGenError(null);
        
        // Preload images before navigating to ensure smooth animation
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(imageIds, companyIdResult);
        } catch (preloadErr) {
          console.warn('[SeleccionarComposicionAnuncio] Image preload failed, continuing anyway:', preloadErr);
        }
        
        setCompleteProgressNow(true);
        // Navigate to dedicated generated images page with preloaded data
        navigate('/redes-sociales/imagenes-generadas', {
          state: { imageIds, companyId: companyIdResult, preloadedImages }
        });
        try { window.sessionStorage.setItem('atlas:crear-anuncio:has-generated', '1'); } catch {}
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e) {
      console.error(e);
      const next = (consecutiveGenErrorsRef.current || 0) + 1;
      consecutiveGenErrorsRef.current = next;
      if (next >= 2) {
        setGenError(
          <span>
            Se produjo un error. No se consumieron imágenes. Es posible que los servidores estén momentaneamente en mantenimento. Si prefieres puedes contactarte con el{' '}
            <button
              type="button"
              onClick={() => navigate('/ayuda')}
              className="font-semibold underline"
            >
              servicio de ayuda
            </button>
            .
          </span>
        );
      } else {
        setGenError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      }
      setCompleteProgressNow(true);
    }
  };

  const handleGenerateMore = async () => {
    try {
      setGenError(null);
      setCompleteProgressNow(false);
      setGenerating(true);
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) { setGenerating(false); return; }
      try { if (textEnabled && compositionText.trim()) trackInteraction('texto_personalizado'); } catch {}

      if (modo === 'personalizado') {
        saveLastCompositionState();
      }

      // 1) Get user and company_id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');

      // 2) Build format description
      let formatName = '';
      switch (formato) {
        case 'story':
          formatName = '9:16';
          break;
        case 'square':
          formatName = '1:1';
          break;
        case 'post':
        default:
          formatName = '4:5';
      }

      // 3) Determine ids to use (use current ids)
      const variantIds = ids.map((s) => String(s)).map((s) => s.trim()).filter((s) => !!s && s !== '0');
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      // 4) Build request payload (same settings, fewer images)
      const composicionForApi: 'modelo' | 'producto' = (!isFashion && modo === 'personalizado') ? 'producto' : composicion;
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo,
        estilo: 'automatico',
        tipo,
        cantidad_imagenes: tipo === 'imagen' ? 2 : undefined,
        Format: formatName,
        objetivo: objetivo || undefined,
        detalles_oferta: detallesOferta.trim() ? detallesOferta.trim() : undefined,
        composicion: composicionForApi,
        background_mode: backgroundMode,
        background_id:
          (backgroundMode === 'elegido' && typeof backgroundId === 'number' && Number.isFinite(backgroundId))
            ? backgroundId
            : (backgroundMode === 'blanco' && modo === 'personalizado' ? 5 : undefined),
        background_url: backgroundMode === 'elegido' && !(typeof backgroundId === 'number' && Number.isFinite(backgroundId)) ? (backgroundUrl || undefined) : undefined,
        background_text: backgroundMode === 'descripcion' && backgroundTextParam.trim() ? backgroundTextParam.trim() : undefined,
        composition_mode: textEnabled ? true : false,
        composition_id: textEnabled && typeof selectedCompId === 'number' && Number.isFinite(selectedCompId) ? selectedCompId : undefined,
        composition_url: textEnabled && !(typeof selectedCompId === 'number' && Number.isFinite(selectedCompId)) ? (selectedCompUrl || undefined) : undefined,
        composition_text: textEnabled && compositionText.trim() ? compositionText.trim() : undefined,
        modo_pro: modoPro,
      };

      // Model details
      if (pose) requestData.pose = pose;
      requestData.modelo_mode = modeloMode;
      if (modeloMode === 'guardados' && modeloGuardadoId) {
        requestData.modelo_guardado_id = modeloGuardadoId;
        if (modeloGuardadoNombre) requestData.modelo_guardado_nombre = modeloGuardadoNombre;
      } else if (modeloMode === 'personalizado') {
        if (genero) requestData.genero = genero;
        if (edad) requestData.edad = edad;
        if (tipoCuerpo) requestData.tipo_cuerpo = tipoCuerpo;
        if (tonoPiel) requestData.tono_piel = tonoPiel;
        if (colorPelo) requestData.color_pelo = colorPelo;
        if (largoPelo) requestData.largo_pelo = largoPelo;
        if (descModelo.trim()) requestData.desc_modelo = descModelo.trim();
      }

      // 5) Call webhook
      try { console.log('[SeleccionarComposicionAnuncio] handleGenerateMore payload', requestData); } catch {}
      const response = await fetch(GENERATE_AD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData)
      });
      let __respTextCGM: string | null = null;
      try { __respTextCGM = await response.clone().text(); } catch {}
      try { console.log('[SeleccionarComposicionAnuncio] handleGenerateMore response', { status: response.status, ok: response.ok, text: __respTextCGM }); } catch {}
      if (!response.ok) {
        console.error('[SeleccionarComposicionAnuncio] handleGenerateMore not ok');
        throw new Error(__respTextCGM || 'Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[SeleccionarComposicionAnuncio] handleGenerateMore json parse error', e); }
      try { console.log('[SeleccionarComposicionAnuncio] handleGenerateMore json', data); } catch {}
      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        willShowResultsRef.current = true;
        const newImageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult = (data.id_company as string) || profile.id_company;
        
        // Preload only the NEW images before navigating
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(newImageIds, companyIdResult);
        } catch (preloadErr) {
          console.warn('[SeleccionarComposicionAnuncio] handleGenerateMore preload failed:', preloadErr);
        }
        
        setGeneratedImageIds((prev) => [...prev, ...newImageIds]);
        setCompanyIdForImages(companyIdResult);
        setCompleteProgressNow(true);
        try { window.sessionStorage.setItem('atlas:crear-anuncio:has-generated', '1'); } catch {}
        // Navigate to dedicated generated images page with all images and preloaded data
        navigate('/redes-sociales/imagenes-generadas', {
          state: { imageIds: [...generatedImageIds, ...newImageIds], companyId: companyIdResult, preloadedImages }
        });
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e) {
      console.error(e);
      const next = (consecutiveGenErrorsRef.current || 0) + 1;
      consecutiveGenErrorsRef.current = next;
      if (next >= 2) {
        setGenError(
          <span>
            Se produjo un error. No se consumieron imágenes. Es posible que los servidores estén momentaneamente en mantenimento. Si prefieres puedes contactarte con el{' '}
            <button
              type="button"
              onClick={() => navigate('/ayuda')}
              className="font-semibold underline"
            >
              servicio de ayuda
            </button>
            .
          </span>
        );
      } else {
        setGenError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      }
      setCompleteProgressNow(true);
    }
  };

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent" style={{ pointerEvents: clickShield ? 'none' : 'auto' }}>
      {generating && (
        <LoadingAnimation
          message="Generando imágenes..."
          durationMs={progressDurationMs}
          completeNow={completeProgressNow}
          onCompleted={() => { if (!willShowResultsRef.current) setGenerating(false); }}
        />
      )}
      {!showResults ? (
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">
        {/* Mobile header */}
        <div className="sm:hidden mt-2 mb-4 text-center">
          <h1 className="text-sm font-semibold text-gray-900">Seleccionar texto</h1>
          <p className="text-sm text-gray-600 mt-3">Modo {modo === 'personalizado' ? 'personalizado' : 'rápido'} · {ids.length} productos seleccionados</p>
        </div>

        {/* Desktop header */}
        <div className="hidden sm:flex items-start justify-between mb-6">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Seleccionar texto</h1>
            <p className="text-sm text-gray-600 mt-1">Modo {modo === 'personalizado' ? 'personalizado' : 'rápido'} · {ids.length} productos seleccionados</p>
          </div>
        </div>
        {genError && (
          <div className="mb-3 bg-red-50 text-red-700 px-4 py-3 text-sm">
            {genError}
          </div>
        )}
        {/* Progress */}
        <div className="mt-4">
          <div className="w-full h-1 bg-gray-200 rounded-full">
            <div className="h-1 bg-blue-500 rounded-full" style={{ width: '95%' }} />
          </div>
        </div>

        {/* Toggle: Añadir texto a la imagen (estilo similar a SeleccionarObjetivo) */}
        <div className="mt-6">
          <div className="w-full text-left border border-gray-200 rounded-2xl p-4 flex items-center gap-4 hover:bg-gray-50">
            <div className="shrink-0 bg-blue-50 p-2 rounded-md">
              <Type className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 id="add-text-label" className="text-sm font-semibold text-gray-900">Añadir texto a la imagen</h2>
              <p className="text-sm text-gray-600 mt-0.5">Activá para añadir un texto usando una plantilla de la biblioteca.</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={textEnabled}
              aria-labelledby="add-text-label"
              onClick={() => setTextEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${textEnabled ? 'bg-blue-500' : 'bg-gray-200'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${textEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* Biblioteca (visible solo cuando el toggle está activo) */}
        {textEnabled && (
          <div className="mt-6">
            {loading && <span className="text-sm text-gray-500">Cargando…</span>}
            {fetchError && <p className="text-sm text-red-600 mt-2">{fetchError}</p>}
            {!loading && compsPorCategoria.length === 0 && (
              <p className="text-sm text-gray-600 mt-2">No hay composiciones disponibles por el momento.</p>
            )}
            {!loading && compsPorCategoria.length > 0 && (
              <>
                {/* Categorías horizontales */}
                <div className="mt-3 overflow-x-auto">
                  <div className="inline-flex gap-2">
                    {categorias.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setActiveCategoria(cat)}
                        className={`px-4 h-9 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 whitespace-nowrap ${activeCategoria === cat ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Grilla para la categoría activa */}
                <div className="mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {itemsCategoriaActual.map((comp) => {
                      const isSelected = (selectedCompId === comp.id);
                      return (
                        <div
                          key={comp.id}
                          className={`w-full text-left border rounded-2xl overflow-hidden py-3 px-2 transition-colors ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500 col-span-2' : 'border-gray-200'}`}
                        >
                          <div className={`flex ${isSelected ? 'flex-col sm:flex-row' : 'flex-col'} gap-3`}>
                            <button
                              type="button"
                              onClick={() => pickComposicion(comp.link_img, comp.id)}
                              className={`relative overflow-hidden rounded-xl h-28 ${isSelected ? 'sm:w-48' : 'w-full'} flex-shrink-0 focus:outline-none hover:bg-gray-50`}
                            >
                              <img src={comp.link_img} alt={comp.nombre || `Composición ${comp.id}`} className="w-full h-full object-cover" />
                              {isSelected && (
                                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white">
                                  <Check className="w-4 h-4" />
                                </span>
                              )}
                            </button>
                            {isSelected && (
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700">Texto</label>
                                <textarea
                                  value={compositionText}
                                  onChange={(e) => setCompositionText(e.target.value)}
                                  placeholder="Escribí tu texto..."
                                  rows={4}
                                  className="mt-1 block w-full rounded-md border-0 resize-none bg-transparent text-sm focus:outline-none"
                                />
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mt-2 truncate">{comp.nombre || 'Composición'}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Footer desktop */}
        <div className="mt-6 hidden sm:flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>
          <button
            type="button"
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${canGenerateNow && !generating ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`}
            onClick={handleGenerate}
            disabled={!canGenerateNow || generating}
          >
            {generating ? (<><Loader2 className="w-4 h-4 animate-spin" /> Generando</>) : (<><Sparkles className="w-4 h-4" /> Generar</>)}
          </button>
        </div>
      </div>
      ) : (
          <GeneratedImagesInline
            imageIds={generatedImageIds}
            companyId={companyIdForImages}
            onReturnToConfig={handleReturnToConfig}
            onGenerateMore={handleGenerateMore}
            onReady={() => { willShowResultsRef.current = false; setGenerating(false); }}
          />
      )}

      {/* Mobile spacer for fixed bottom bar */}
      {!showResults && (
        <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />
      )}

      {/* Mobile bottom bar */}
      {!showResults && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Volver
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${canGenerateNow && !generating ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`}
              onClick={handleGenerate}
              disabled={!canGenerateNow || generating}
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Generando
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" /> Generar
                </>
              )}
            </button>
          </div>
        </div>
      )}
      {/* Subscription limit modal */}
      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
      />
    </div>
  );
};

export default SeleccionarComposicionAnuncio;
